import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import { getAttempts, getAttemptAnswers, cancelAttempt } from "../../../services/StudentAttemptService";
import { getExamSessions } from "../../../services/ExamSessionService";
import { getExams } from "../../../services/ExamService";
import { getStudentProfiles } from "../../../services/StudentProfileService";
import { getUsers } from "../../../services/UserService";
import { getRooms } from "../../../services/RoomService";
import { getTablets } from "../../../services/TabletService";
import DataTablePagination from "../common/DataTablePagination";
import { useTranslation } from "react-i18next";
import { formatDuration, getLocale, lazyLabels } from "../../../utils/i18nLabels";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";

const statusLabels = lazyLabels({ in_progress: "common.attemptStatus.in_progress", submitted: "common.attemptStatus.submitted", timed_out: "common.attemptStatus.timed_out", cancelled: "common.attemptStatus.cancelled" });
const statusBadge = { in_progress: "primary", submitted: "success", timed_out: "warning", cancelled: "danger" };
const LIVE_REFRESH_MS = 10000;
// Réponse réunion de suivi (15/11) : "Vérifier comment la direction des
// études voit les étudiants connectés et entre/sort de la session
// d'examen." - un heartbeat plus ancien que ce seuil est considéré comme
// une déconnexion (voir services/studentAttempts.service.js côté backend,
// qui utilise le même mécanisme pour créditer le temps de coupure).
const ONLINE_THRESHOLD_MS = 45000;


const Attempts = () => {
  const { t } = useTranslation();
  const can = usePermissions();
  const canCancel = can(PERMISSIONS.STUDENT_ATTEMPT_CANCEL);
  const [searchParams] = useSearchParams();
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [liveMode, setLiveMode] = useState(true);

  const [examSessions, setExamSessions] = useState([]);
  const [exams, setExams] = useState([]);
  const [students, setStudents] = useState([]);
  const [users, setUsers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [tablets, setTablets] = useState([]);
  const [nowTick, setNowTick] = useState(Date.now());

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [sessionFilter, setSessionFilter] = useState(searchParams.get("exam_session_id") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [feedback, setFeedback] = useState(null);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const [viewingAttempt, setViewingAttempt] = useState(null);
  const [attemptAnswers, setAttemptAnswers] = useState([]);
  const [loadingAnswers, setLoadingAnswers] = useState(false);

  useEffect(() => {
    getExamSessions({ page: 1, limit: 200 }).then((res) => setExamSessions(res.data?.data || [])).catch(() => { });
    getExams({ page: 1, limit: 200 }).then((res) => setExams(res.data?.data || [])).catch(() => { });
    getStudentProfiles({ page: 1, limit: 200 }).then((res) => setStudents(res.data?.data || [])).catch(() => { });
    getUsers({ page: 1, limit: 200 }).then((res) => setUsers(res.data?.data || [])).catch(() => { });
    getRooms({ page: 1, limit: 200 }).then((res) => setRooms(res.data?.data || [])).catch(() => { });
    getTablets({ page: 1, limit: 200 }).then((res) => setTablets(res.data?.data || [])).catch(() => { });
  }, []);

  // Pour rafraîchir le badge "en ligne / hors ligne" même quand la liste
  // elle-même n'a pas changé entre deux appels silencieux.
  useEffect(() => {
    if (!liveMode) return undefined;
    const t = setInterval(() => setNowTick(Date.now()), 5000);
    return () => clearInterval(t);
  }, [liveMode]);

  const fetchAttempts = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: "started_at", sort_dir: "desc" };
      if (sessionFilter) params.exam_session_id = sessionFilter;
      if (statusFilter) params.status = statusFilter;

      const res = await getAttempts(params);
      const body = res.data;
      setAttempts(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      if (!silent) {
        setLoadError(err?.response?.data?.message || t("pages.attempts.impossibleChargerTentatives"));
        setAttempts([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, limit, sessionFilter, statusFilter, t]);

  useEffect(() => {
    fetchAttempts();
  }, [fetchAttempts]);

  // Suivi en direct : tant qu'il y a des tentatives en cours, on rafraîchit
  // la liste régulièrement en arrière-plan (sans réafficher le spinner).
  useEffect(() => {
    if (!liveMode) return undefined;
    const interval = setInterval(() => fetchAttempts(true), LIVE_REFRESH_MS);
    return () => clearInterval(interval);
  }, [liveMode, fetchAttempts]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const sessionLabel = (id) => {
    const session = examSessions.find((s) => s.id === id);
    if (!session) return "-";
    const exam = exams.find((e) => e.id === session.exam_id);
    return exam ? `${exam.title} (${new Date(session.start_at).toLocaleDateString(getLocale())})` : id;
  };

  const studentLabel = (id) => {
    const profile = students.find((s) => s.id === id);
    if (!profile) return "-";
    const user = users.find((u) => u.id === profile.user_id);
    return user ? `${user.email} (${profile.matricule})` : profile.matricule;
  };

  const roomLabel = (id) => rooms.find((r) => r.id === id)?.name || (id ? "-" : "-");
  const tabletLabel = (id) => tablets.find((t) => t.id === id)?.mac_address || (id ? "-" : "-");

  const connectionStatus = (attempt) => {
    if (attempt.status !== "in_progress") return null;
    if (!attempt.last_heartbeat_at) return { label: t("pages.attempts.enAttente"), badge: "secondary" };
    const age = nowTick - new Date(attempt.last_heartbeat_at).getTime();
    return age <= ONLINE_THRESHOLD_MS
      ? { label: t("pages.attempts.enLigne"), badge: "success" }
      : { label: t("pages.attempts.horsLigne"), badge: "danger" };
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await cancelAttempt(cancelTarget.id);
      setFeedback({ type: "success", message: t("pages.attempts.cancelSuccess") });
      setCancelTarget(null);
      fetchAttempts();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.attempts.cancelError") });
      setCancelTarget(null);
    } finally {
      setCancelling(false);
    }
  };

  const openAnswers = async (attempt) => {
    setViewingAttempt(attempt);
    setLoadingAnswers(true);
    try {
      const res = await getAttemptAnswers(attempt.id);
      setAttemptAnswers(res.data?.data || []);
    } catch (err) {
      setFeedback({ type: "danger", message: t("pages.attempts.loadAnswersError") });
    } finally {
      setLoadingAnswers(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("pages.attempts.breadcrumb")}</li>
              </ol>
            </nav>
          </div>
          <div className="mb-2 form-check form-switch d-flex align-items-center">
            <input
              className="form-check-input me-2"
              type="checkbox"
              role="switch"
              id="live-mode"
              checked={liveMode}
              onChange={(e) => setLiveMode(e.target.checked)}
            />
            <label className="form-check-label d-flex align-items-center" htmlFor="live-mode">
              {liveMode && (
                <span className="badge bg-danger-transparent text-danger me-2 d-inline-flex align-items-center">
                  <i className="ti ti-circle-filled fs-8 me-1"></i>{t("pages.attempts.liveIndicator")}
                </span>
              )}
              {t("pages.attempts.liveTracking")}
            </label>
          </div>
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("pages.attempts.title")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="mb-3 me-2">
                <select className="form-select" value={sessionFilter} onChange={(e) => { setSessionFilter(e.target.value); setPage(1); }}>
                  <option value="">{t("common.ui.toutesSessions")}</option>
                  {examSessions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {sessionLabel(s.id)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3 me-2">
                <select className="form-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                  <option value="">{t("common.crud.allStatuses")}</option>
                  <option value="in_progress">{t("common.examSessionStatus.ongoing")}</option>
                  <option value="submitted">{t("common.attemptStatus.submitted")}</option>
                  <option value="timed_out">{t("common.attemptStatus.timed_out")}</option>
                  <option value="cancelled">{t("common.examSessionStatus.cancelled")}</option>
                </select>
              </div>
            </div>
          </div>

          <DataTablePagination
            variant="header"
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={(v) => { setLimit(v); setPage(1); }}
          />

          <div className="card-body p-0 py-3">
            <div className="custom-datatable-filter table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th>{t("roles.STUDENT")}</th>
                    <th>{t("common.ui.sessionExamen")}</th>
                    <th>{t("pages.attempts.salleTablette")}</th>
                    <th>{t("pages.attempts.connexion")}</th>
                    <th>{t("pages.attempts.startedAt")}</th>
                    <th>{t("pages.attempts.submittedAt")}</th>
                    <th>{t("common.ui.tempsPasse")}</th>
                    <th>{t("common.crud.statusColumn")}</th>
                    <th>{t("common.crud.actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan="9" className="text-center py-4 text-danger">
                        {loadError}
                      </td>
                    </tr>
                  ) : attempts.length > 0 ? (
                    attempts.map((attempt) => (
                      <tr key={attempt.id}>
                        <td>{studentLabel(attempt.student_id)}</td>
                        <td>{sessionLabel(attempt.exam_session_id)}</td>
                        <td>
                          {attempt.room_id || attempt.tablet_id ? (
                            <>
                              {attempt.room_id ? roomLabel(attempt.room_id) : "-"}
                              {attempt.tablet_id ? ` / ${tabletLabel(attempt.tablet_id)}` : ""}
                            </>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {connectionStatus(attempt) ? (
                            <span className={`badge badge-soft-${connectionStatus(attempt).badge}`}>
                              {connectionStatus(attempt).label}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>{attempt.started_at ? new Date(attempt.started_at).toLocaleString(getLocale()) : "-"}</td>
                        <td>{attempt.submitted_at ? new Date(attempt.submitted_at).toLocaleString(getLocale()) : "-"}</td>
                        <td>
                          {attempt.status === "in_progress" ? (
                            <span className="text-muted">{t("pages.attempts.inProgressLabel")}</span>
                          ) : (
                            formatDuration(attempt.time_spent_seconds)
                          )}
                        </td>
                        <td>
                          <span className={`badge badge-soft-${statusBadge[attempt.status] || "secondary"}`}>
                            {statusLabels[attempt.status] || attempt.status}
                          </span>
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              as="button"
                              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false"
                            >
                              <i className="ti ti-dots-vertical fs-14"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="p-3">
                              <Dropdown.Item className="rounded-1" onClick={() => openAnswers(attempt)}>
                                <i className="ti ti-eye me-2"></i>{t("pages.attempts.viewAnswers")}
                              </Dropdown.Item>
                              {canCancel && attempt.status === "in_progress" && (
                                <Dropdown.Item className="rounded-1" onClick={() => setCancelTarget(attempt)}>
                                  <i className="ti ti-ban me-2"></i>{t("common.crud.cancel")}
                                </Dropdown.Item>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <i className="ti ti-search-off fs-1 text-muted"></i>
                        <h5 className="mt-2">{t("pages.attempts.noAttemptsFound")}</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <DataTablePagination
              variant="footer"
              page={page}
              limit={limit}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onLimitChange={(v) => { setLimit(v); setPage(1); }}
            />
          </div>
        </div>
      </div>

      <Modal show={cancelTarget !== null} onHide={() => (cancelling ? null : setCancelTarget(null))} centered>
        <div className="modal-body text-center">
          <span className="delete-icon">
            <i className="ti ti-ban"></i>
          </span>
          <h4>{t("pages.attempts.confirmCancelTitle")}</h4>
          <p>{t("pages.attempts.confirmCancelBody")}</p>
          <div className="d-flex justify-content-center">
            <button type="button" className="btn btn-light me-3" onClick={() => setCancelTarget(null)} disabled={cancelling}>
              {t("pages.attempts.back")}
            </button>
            <button type="button" className="btn btn-danger" onClick={confirmCancel} disabled={cancelling}>
              {cancelling ? t("pages.attempts.cancelling") : t("pages.attempts.confirmCancel")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        show={viewingAttempt !== null}
        onHide={() => setViewingAttempt(null)}
        centered
        size="lg"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("pages.attempts.answersTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingAnswers ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t("common.crud.loading")}</span>
              </div>
            </div>
          ) : attemptAnswers.length > 0 ? (
            <div className="table-responsive">
              <table
                className="table table-sm align-middle"
                style={{
                  width: "100%",
                  tableLayout: "fixed",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: "65%" }}>
                      {t("common.ui.question")}
                    </th>

                    <th
                      style={{
                        width: "18%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("pages.attempts.correctColumn")}
                    </th>

                    <th
                      style={{
                        width: "17%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("pages.attempts.pointsEarned")}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {attemptAnswers.map((answer) => (
                    <tr key={answer.id}>
                      <td
                        style={{
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        }}
                      >
                        <div
                          style={{
                            maxWidth: "100%",
                            whiteSpace: "normal",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          {answer.question_content}
                        </div>
                      </td>

                      <td style={{ whiteSpace: "nowrap" }}>
                        {answer.is_correct === null ? (
                          "-"
                        ) : (
                          <span
                            className={`badge badge-soft-${answer.is_correct ? "success" : "danger"
                              }`}
                          >
                            {answer.is_correct
                              ? t("common.fields.yes")
                              : t("common.fields.no")}
                          </span>
                        )}
                      </td>

                      <td style={{ whiteSpace: "nowrap" }}>
                        {answer.points_earned ?? "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted text-center py-3">{t("pages.attempts.noAnswersRecorded")}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-light" onClick={() => setViewingAttempt(null)}>
            {t("common.crud.close")}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Attempts;
